import type { ReactNode } from 'react';
import { CheckCircle2, XCircle, Info, TriangleAlert, ShieldX, FileQuestion, ServerCrash } from 'lucide-react';
import { cn } from './cn';

type Status = 'success' | 'error' | 'info' | 'warning' | '403' | '404' | '500';

const MAP: Record<Status, { icon: ReactNode; color: string }> = {
  success: { icon: <CheckCircle2 />, color: 'text-brand-600' },
  error: { icon: <XCircle />, color: 'text-danger-500' },
  info: { icon: <Info />, color: 'text-sky-500' },
  warning: { icon: <TriangleAlert />, color: 'text-warning-500' },
  '403': { icon: <ShieldX />, color: 'text-danger-500' },
  '404': { icon: <FileQuestion />, color: 'text-subtle' },
  '500': { icon: <ServerCrash />, color: 'text-danger-500' },
};

/** Ant Design `Result` replacement — status icon + title + subtitle + actions. */
export function Result({
  status = 'info', title, subTitle, icon, extra, className, children,
}: {
  status?: Status;
  title?: ReactNode;
  subTitle?: ReactNode;
  icon?: ReactNode;
  extra?: ReactNode;
  className?: string;
  children?: ReactNode;
}) {
  const m = MAP[status] ?? MAP.info;
  return (
    <div className={cn('flex flex-col items-center px-6 py-12 text-center', className)}>
      <span className={cn('mb-5 [&>svg]:size-14', m.color)}>{icon ?? m.icon}</span>
      {title != null && <div className="text-xl font-semibold text-content">{title}</div>}
      {subTitle != null && <p className="mt-2 max-w-md text-sm leading-relaxed text-muted">{subTitle}</p>}
      {extra != null && <div className="mt-6 flex flex-wrap items-center justify-center gap-2">{extra}</div>}
      {children}
    </div>
  );
}
